import { useEffect, useRef, useState } from "react";
import type React from "react";
import "./PMSidebarModules.css";

// PMSidebar, extended with Modules. Pending upstream to PropelMapper/design
// (docs/design-gaps.md #15). Same anatomy and tokens as PMSidebar:
// dark --pm-surface-inverse chrome, custom icon set (outline inactive,
// filled active), eyebrow groups, team card, bell + log out.
// Adds:
//  · Modules nested under a Section (a view or action inside it), with counts
//  · the active Section expands; others expand on demand (chevron)
//  · rail variant: hovering or focusing a Section opens a flyout of its Modules
//  · real handlers for notifications and log out

export interface PMModuleItem { id: string; label: string; count?: number; tone?: "alert" | "info"; action?: boolean }
export interface PMSectionItem { id: string; icon: string; label: string; group?: string; modules: PMModuleItem[] }

export interface PMSidebarModulesProps {
  variant?: "full" | "rail";
  sections: PMSectionItem[];
  activeSection?: string;
  activeModule?: string | null;
  team?: { name: string; initial: string } | null;
  notificationCount?: number;
  collapsible?: boolean;
  onSection: (s: PMSectionItem) => void;
  onModule: (s: PMSectionItem, m: PMModuleItem) => void;
  onToggleCollapse?: () => void;
  onNotifications?: () => void;
  onLogout?: () => void;
  /** Rendered under the logo (e.g. the global search button). */
  top?: React.ReactNode;
  /** Rendered above the bell/log out row (e.g. sync state, account). */
  footer?: React.ReactNode;
}

const MARK = '<svg viewBox="0 0 102 156" width="29" height="44" fill="currentColor" aria-hidden="true"><rect rx="18.6" x="0" y="0" width="101.78" height="37.21"></rect><rect rx="19.5" x="0" y="73" width="39" height="83"></rect><rect rx="18.6" x="45.96" y="71.13" width="55.81" height="37.21"></rect><rect rx="18.5" x="65" y="0" width="37" height="108"></rect></svg>';

function Glyph({ name, active = false, size = 22 }: { name: string; active?: boolean; size?: number }) {
  const I = (typeof window !== "undefined" && (window as any).PM_ICONS) || {};
  const pick = active ? [name + "-filled", name + "-outline", name] : [name + "-outline", name + "-filled", name];
  const svg = pick.map((n) => I[n]).find(Boolean);
  if (!svg) return <span className="pmsm-ic" style={{ width: size, height: size }} aria-hidden="true" />;
  return <span className="pmsm-ic" aria-hidden="true" style={{ width: size, height: size }} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function PMSidebarModules(p: PMSidebarModulesProps) {
  const rail = p.variant === "rail";
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [fly, setFly] = useState<string | null>(null);
  const flyTimer = useRef<number | undefined>(undefined);
  const root = useRef<HTMLElement>(null);

  // The active section is always expanded; remember manual toggles for others.
  const expanded = (id: string) => (open[id] ?? id === p.activeSection);
  useEffect(() => { setFly(null); }, [p.activeSection, p.activeModule]);
  useEffect(() => {
    if (!fly) return;
    const off = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setFly(null); };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setFly(null);
    addEventListener("mousedown", off); addEventListener("keydown", esc);
    return () => { removeEventListener("mousedown", off); removeEventListener("keydown", esc); };
  }, [fly]);

  const groups: { label?: string; items: PMSectionItem[] }[] = [];
  for (const s of p.sections) {
    const g = groups.at(-1);
    if (g && g.label === s.group) g.items.push(s); else groups.push({ label: s.group, items: [s] });
  }
  const total = (s: PMSectionItem) => s.modules.filter((m) => m.tone === "alert").reduce((a, m) => a + (m.count || 0), 0);

  const Modules = ({ s, inFly = false }: { s: PMSectionItem; inFly?: boolean }) => (
    <ul className={`pmsm-mods ${inFly ? "pmsm-mods--fly" : ""}`} role="list">
      {s.modules.map((m) => {
        const on = !m.action && s.id === p.activeSection && m.id === p.activeModule;
        return (
          <li key={m.id}>
            <button type="button" className={`pmsm-mod ${on ? "is-active" : ""} ${m.action ? "pmsm-mod--action" : ""}`} aria-current={on ? "page" : undefined} onClick={() => p.onModule(s, m)}>
              {m.action ? <span className="pmsm-plus" aria-hidden="true">+</span> : <span className="pmsm-dot" aria-hidden="true" />}
              <span className="pmsm-mlabel">{m.label}</span>
              {m.count ? <span className={`pmsm-count ${m.tone ? `pmsm-count--${m.tone}` : ""}`}>{m.count > 99 ? "99+" : m.count}</span> : null}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <aside ref={root} className={`pmsm ${rail ? "pmsm--rail" : ""}`} aria-label="Primary">
      <div className="pmsm-logo">
        <span className="pmsm-mark" dangerouslySetInnerHTML={{ __html: MARK }} />
        {!rail && <span className="pmsm-word">PROPEL{"\n"}MAPPER</span>}
        {p.collapsible && (
          <button type="button" className="pmsm-collapse" onClick={p.onToggleCollapse} aria-label={rail ? "Expand navigation" : "Collapse navigation"} title={rail ? "Expand" : "Collapse"}>
            <Glyph name={rail ? "chevron-right" : "chevron-left"} size={20} />
          </button>
        )}
      </div>

      {p.top && <div className="pmsm-top">{p.top}</div>}
      <nav className="pmsm-nav">
        {groups.map((g, gi) => (
          <div key={gi} className="pmsm-group">
            {g.label && !rail && <div className="pmsm-eyebrow">{g.label}</div>}
            {g.label && rail && <div className="pmsm-div" />}
            {g.items.map((s) => {
              const active = s.id === p.activeSection;
              const has = s.modules.length > 0;
              const ex = has && expanded(s.id);
              const badge = total(s);
              return (
                <div key={s.id} className={`pmsm-sec ${active ? "is-active" : ""}`}
                  onMouseEnter={() => { if (rail && has) { clearTimeout(flyTimer.current); setFly(s.id); } }}
                  onMouseLeave={() => { if (rail) flyTimer.current = window.setTimeout(() => setFly((f) => (f === s.id ? null : f)), 180); }}>
                  <div className="pmsm-row">
                    <button type="button" className={`pmsm-item ${active ? "is-active" : ""}`} aria-current={active && !has ? "page" : undefined} title={rail ? s.label : undefined}
                      onClick={() => { p.onSection(s); if (has && !rail) setOpen((o) => ({ ...o, [s.id]: true })); }}
                      onFocus={() => rail && has && setFly(s.id)}>
                      <Glyph name={s.icon} active={active} />
                      {!rail && <span className="pmsm-label">{s.label}</span>}
                      {badge > 0 && (rail || !ex) && <span className={`pmsm-badge ${rail ? "pmsm-badge--rail" : ""}`} aria-label={`${badge} need attention`}>{badge}</span>}
                    </button>
                    {has && !rail && (
                      <button type="button" className={`pmsm-chev ${ex ? "is-open" : ""}`} aria-label={`${ex ? "Collapse" : "Expand"} ${s.label}`} aria-expanded={ex}
                        onClick={() => setOpen((o) => ({ ...o, [s.id]: !ex }))}>
                        <Glyph name="chevron-down" size={16} />
                      </button>
                    )}
                  </div>
                  {has && !rail && <div className={`pmsm-drawer ${ex ? "is-open" : ""}`}><div><Modules s={s} /></div></div>}
                  {rail && fly === s.id && (
                    <div className="pmsm-fly" role="menu" aria-label={s.label}>
                      <div className="pmsm-fly-title">{s.label}</div>
                      <Modules s={s} inFly />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="pmsm-grow" />
      {p.team && !rail && (
        <div className="pmsm-team">
          <div className="pmsm-team-av">{p.team.initial}</div>
          <div><div className="pmsm-team-eyebrow">Team</div><div className="pmsm-team-name">{p.team.name}</div></div>
        </div>
      )}
      {p.footer && <div className="pmsm-footer">{p.footer}</div>}
      <div className="pmsm-bottom">
        <button type="button" className="pmsm-bell" onClick={p.onNotifications} aria-label="Notifications">
          <Glyph name="bell" size={22} />
          {p.notificationCount ? <span className="pmsm-bell-badge">{p.notificationCount > 99 ? "99+" : p.notificationCount}</span> : null}
        </button>
        {!rail && <button type="button" className="pmsm-logout" onClick={p.onLogout}><Glyph name="arrow-left" size={20} />Log out</button>}
      </div>
    </aside>
  );
}
